// UX-Vorschlag P1: rechte Spalte von „Mein Tag" — „Braucht dich" und
// „Diese Woche". Nur gerendert, wenn uxEnabled("ux_web_p1"); classic sieht
// diese Komponente nie.
//
// Jede Zeile hat GENAU eine Aktion (→ Heute, Antworten, Öffnen). Laden schlaegt
// sichtbar fehl (api() toastet GET-Fehler nicht): bei Fehler steht ein Hinweis
// mit „Erneut laden" da, kein „nichts braucht dich".

import { useEffect, useState } from "preact/hooks";
import { Link, useLocation } from "wouter-preact";
import { ArrowRight, RefreshCw } from "lucide-preact";
import { Button } from "../ui/Button";
import {
  buildNeedsYou,
  buildWeekPreview,
  overdueRefs,
  type NeedsYouRow,
  type WeekRow,
} from "../../lib/needsYou";
import { fetchNeedsYouSources, replyToInboxItem, type NeedsYouSources } from "../../lib/uxApi";
import { refreshInboxCount } from "../../lib/inboxBadge";
import { toastSuccess } from "../../lib/toast";
import { openPersonalTaskSig, openProjectTaskSig } from "../../store/deepLinks";

const VISIBLE_ROWS = 6;

const DOT: Record<NeedsYouRow["kind"], string> = {
  overdue: "bg-[color:var(--color-error)]",
  blocked: "bg-[color:var(--color-warning)]",
  mention: "bg-[color:var(--color-primary)]",
  follow_up: "bg-[color:var(--color-on-surface-variant)]",
};

const ACTION_LABEL: Record<NeedsYouRow["action"], string> = {
  today: "→ Heute",
  reply: "Antworten",
  open: "Öffnen",
};

export interface NeedsYouPanelProps {
  day: string;
  /** Schluessel „task-<id>"/„personal-<id>" aller Eintraege in „Heute". */
  todayKeys: Set<string>;
  /** Legt in „Heute" an; true bei Erfolg. Fehler toastet api() (Mutation). */
  onAddToday: (kind: "task" | "personal", id: number) => Promise<boolean>;
  /** Mit P3: Aufgabe im Detailbereich von „Mein Tag" oeffnen statt die Seite zu wechseln. */
  onOpenTask?: (kind: "task" | "personal", id: number, projectId: number | null) => void;
}

export function NeedsYouPanel({ day, todayKeys, onAddToday, onOpenTask }: NeedsYouPanelProps): preact.JSX.Element {
  const [, setLocation] = useLocation();
  const [sources, setSources] = useState<NeedsYouSources | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [replyKey, setReplyKey] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setSources(await fetchNeedsYouSources(day));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  useEffect(() => {
    const onRefresh = (): void => void load();
    window.addEventListener("juuplan:inbox-refresh", onRefresh);
    return () => window.removeEventListener("juuplan:inbox-refresh", onRefresh);
  }, []);

  const rows = sources ? buildNeedsYou({ day, todayKeys, needsYou: sources.needsYou }) : [];
  const week: WeekRow[] = sources
    ? buildWeekPreview({
        day,
        todayKeys,
        projectTasks: sources.projectTasks,
        personalTasks: sources.personalTasks,
      })
    : [];
  const overdue = overdueRefs(rows);
  const shown = showAll ? rows : rows.slice(0, VISIBLE_ROWS);

  const open = (ref: NeedsYouRow["ref"]): void => {
    if (onOpenTask && (ref.kind === "personal" || (ref.kind === "task" && ref.project_id !== null))) {
      onOpenTask(ref.kind, ref.id, ref.project_id);
    } else if (ref.kind === "task" && ref.project_id !== null) {
      openProjectTaskSig.value = ref.id;
      setLocation(`/p/${ref.project_id}`);
    } else if (ref.kind === "personal") {
      openPersonalTaskSig.value = ref.id;
      setLocation("/lists");
    } else if (ref.link) {
      setLocation(ref.link);
    }
  };

  const openWeek = (r: WeekRow): void =>
    open({ kind: r.kind, id: r.id, project_id: r.project_id, task_id: null, link: null });

  const runAction = async (row: NeedsYouRow): Promise<void> => {
    if (row.action === "today" && (row.ref.kind === "task" || row.ref.kind === "personal")) {
      setBusyKey(row.key);
      try {
        await onAddToday(row.ref.kind, row.ref.id);
      } finally {
        setBusyKey(null);
      }
      return;
    }
    if (row.action === "reply") {
      setReplyKey(replyKey === row.key ? null : row.key);
      setReplyText("");
      return;
    }
    open(row.ref);
  };

  const sendReply = async (row: NeedsYouRow): Promise<void> => {
    const text = replyText.trim();
    if (!text || !row.ref.source_type) return;
    setBusyKey(row.key);
    try {
      // Kommentar + Eintrag erledigt in einem Aufruf: die Erwaehnung verlaesst
      // Inbox und Glocke.
      await replyToInboxItem(row.ref.source_type, row.ref.id, text);
      refreshInboxCount();
      toastSuccess("Antwort gesendet");
      setReplyKey(null);
      setReplyText("");
      await load();
    } catch {
      /* Mutation: api() hat schon getoastet */
    } finally {
      setBusyKey(null);
    }
  };

  const addAllOverdue = async (): Promise<void> => {
    setBulkBusy(true);
    try {
      for (const ref of overdue) {
        if (ref.kind === "task" || ref.kind === "personal") {
          // Nacheinander: POST /api/my-day ist idempotent, aber jede Antwort
          // laedt den Tag neu — parallel wuerden die Reloads sich ueberholen.
          const ok = await onAddToday(ref.kind, ref.id);
          if (!ok) break;
        }
      }
    } finally {
      setBulkBusy(false);
    }
  };

  const header = "text-xs font-semibold tracking-widest uppercase";

  return (
    <div class="flex flex-col gap-4">
      <section
        aria-label="Braucht dich"
        class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]"
      >
        <div class="flex items-center gap-2 border-b border-[color:var(--color-outline-variant)] px-3 py-2">
          <h2 class={`${header} flex-1 text-[color:var(--color-error)]`}>Braucht dich</h2>
          {overdue.length >= 2 && (
            <Button
              variant="quiet"
              size="xs"
              loading={bulkBusy}
              onClick={() => void addAllOverdue()}
            >
              Alle überfälligen übernehmen
            </Button>
          )}
          <span class="text-xs tabular-nums text-[color:var(--color-on-surface-variant)]">
            {sources ? rows.length : ""}
          </span>
        </div>

        {error ? (
          <div class="flex items-center gap-2 px-3 py-3 text-sm text-[color:var(--color-error)]">
            <span class="flex-1">Konnte nicht geladen werden: {error}</span>
            <Button variant="secondary" size="xs" icon={RefreshCw} onClick={() => void load()}>
              Erneut laden
            </Button>
          </div>
        ) : loading && !sources ? (
          <p class="px-3 py-3 text-sm italic text-[color:var(--color-on-surface-variant)]">Lädt…</p>
        ) : rows.length === 0 ? (
          <p class="px-3 py-3 text-sm text-[color:var(--color-on-surface-variant)]">
            Nichts Dringendes — nichts überfällig, keine offenen Erwähnungen.
          </p>
        ) : (
          <ul class="divide-y divide-[color:var(--color-outline-variant)]">
            {shown.map((row) => (
              <li key={row.key} class="px-3 py-2">
                <div class="flex items-start gap-2">
                  <span class={`mt-1.5 size-2 shrink-0 rounded-full ${DOT[row.kind]}`} aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => open(row.ref)}
                    class="min-w-0 flex-1 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    <span class="block text-sm text-[color:var(--color-on-surface)]">{row.title}</span>
                    {row.quote && (
                      <span class="mt-0.5 block line-clamp-2 text-xs italic text-[color:var(--color-on-surface-variant)]">
                        „{row.quote}"
                      </span>
                    )}
                    <span class="mt-0.5 block text-xs text-[color:var(--color-on-surface-variant)]">
                      {row.context}
                    </span>
                  </button>
                  <Button
                    variant="secondary"
                    size="xs"
                    loading={busyKey === row.key && row.action !== "reply"}
                    onClick={() => void runAction(row)}
                    aria-label={`${ACTION_LABEL[row.action]}: ${row.title}`}
                  >
                    {ACTION_LABEL[row.action]}
                  </Button>
                </div>
                {replyKey === row.key && (
                  <form
                    class="mt-2 flex gap-2 pl-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void sendReply(row);
                    }}
                  >
                    <input
                      type="text"
                      value={replyText}
                      onInput={(e) => setReplyText((e.target as HTMLInputElement).value)}
                      placeholder="Antwort als Kommentar …"
                      aria-label="Antwort"
                      class="min-w-0 flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] sm:text-sm"
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      size="xs"
                      loading={busyKey === row.key}
                      disabled={replyText.trim() === ""}
                    >
                      Senden
                    </Button>
                  </form>
                )}
              </li>
            ))}
          </ul>
        )}
        {rows.length > VISIBLE_ROWS && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            class="w-full border-t border-[color:var(--color-outline-variant)] px-3 py-2 text-left text-xs text-[color:var(--color-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          >
            {showAll ? "Weniger anzeigen" : `${rows.length - VISIBLE_ROWS} weitere anzeigen`}
          </button>
        )}
      </section>

      <section
        aria-label="Diese Woche"
        class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]"
      >
        <div class="flex items-center border-b border-[color:var(--color-outline-variant)] px-3 py-2">
          <h2 class={`${header} flex-1 text-[color:var(--color-on-surface-variant)]`}>Diese Woche</h2>
          <span class="text-xs tabular-nums text-[color:var(--color-on-surface-variant)]">
            {sources ? week.length : ""}
          </span>
        </div>
        {error ? null : week.length === 0 ? (
          <p class="px-3 py-3 text-sm text-[color:var(--color-on-surface-variant)]">
            {sources ? "Diese Woche ist sonst nichts fällig." : "Lädt…"}
          </p>
        ) : (
          <ul class="divide-y divide-[color:var(--color-outline-variant)]">
            {week.slice(0, 5).map((r) => (
              <li key={r.key}>
                <button
                  type="button"
                  onClick={() => openWeek(r)}
                  class="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                >
                  <span class="min-w-0 flex-1 truncate text-sm text-[color:var(--color-on-surface)]">{r.title}</span>
                  <span class="shrink-0 text-xs tabular-nums text-[color:var(--color-on-surface-variant)]">
                    {r.dueLabel}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Link
          href="/my-tasks"
          class="flex items-center gap-1 border-t border-[color:var(--color-outline-variant)] px-3 py-2 text-xs text-[color:var(--color-primary)] hover:underline"
        >
          Alle anzeigen in „Meine Aufgaben"
          <ArrowRight size={12} aria-hidden="true" />
        </Link>
      </section>
    </div>
  );
}
